import { ReloadOutlined } from '@ant-design/icons';
import type { StockRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayDate, formatQty, parseApiTime } from '@hq/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { sumWidth } from '@hq/ui-kit';
import { hq } from '../session';

/**
 * 货位维度库存（WMS 最小仓内作业）：
 * `wl_stock.location_id` 已按货位落库，本页把「仓库 → 货位 → 批次/效期」摊开，
 * 便于拣货找货与验收时证明「我们有货位管理」，不做上架/复核写操作。
 */
export default function WmsStock() {
  const [warehouseId, setWarehouseId] = useState('');

  const warehouses = useQuery({
    queryKey: ['wms', 'stock', 'warehouses'],
    queryFn: () => hq.orgStructure.warehouse.list({ status: 'ACTIVE' }),
    staleTime: 5 * 60 * 1000
  });

  const options = useMemo(
    () => (warehouses.data ?? []).map((row) => ({ value: row.id, label: `${row.whName}${row.whKind ? `（${row.whKind}）` : ''}` })),
    [warehouses.data]
  );

  useEffect(() => {
    if (!warehouseId && options.length > 0) setWarehouseId(options[0]!.value);
  }, [options, warehouseId]);

  const locations = useQuery({
    queryKey: ['wms', 'stock', 'locations', warehouseId],
    enabled: Boolean(warehouseId),
    queryFn: () => hq.orgStructure.location.list({ warehouseId }),
    staleTime: 5 * 60 * 1000
  });

  const stock = useQuery({
    queryKey: ['wms', 'stock', 'list', warehouseId],
    enabled: Boolean(warehouseId),
    placeholderData: keepPreviousData,
    queryFn: () => hq.stock.list({ warehouseId, size: 200 })
  });

  const locationMap = useMemo(() => {
    const map = new Map<string, string>();
    (locations.data ?? []).forEach((row) => map.set(row.id, `${row.locationCode}${row.zoneType ? ` · ${row.zoneType}` : ''}`));
    return map;
  }, [locations.data]);

  const rows = stock.data?.items ?? [];
  const totalAmount = rows.reduce((sum, row) => sum + row.totalAmount, 0);
  const noLocation = rows.filter((row) => row.locationId === '0' || !row.locationId).length;
  const skuCount = new Set(rows.map((row) => row.materialId)).size;
  // size=200 单页且无续拉：达上限时上方统计口径不完整，必须提示而不是静默少计
  const truncated = rows.length >= 200;

  const columns: ColumnsType<StockRow> = [
    {
      title: '货位',
      dataIndex: 'locationId',
      width: 180,
      render: (value: string) =>
        value && value !== '0' ? <Tag color="blue">{locationMap.get(value) ?? value}</Tag> : <Tag>未分货位</Tag>
    },
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', width: 200, ellipsis: true, render: (value: string | null) => value || '-' },
    { title: '规格', dataIndex: 'spec', width: 140, render: (value: string | null) => value || '-' },
    { title: '批次', dataIndex: 'batchNo', width: 130, render: (value: string | null) => value || '-' },
    {
      title: '效期',
      dataIndex: 'expiryDate',
      width: 130,
      render: (value: string | null) => {
        const date = parseApiTime(value ?? undefined);
        if (!date) return '-';
        const days = dayjs(date).diff(dayjs(), 'day');
        if (days < 0) return <Tag color="red">{formatDisplayDate(date)}</Tag>;
        if (days <= 90) return <Tag color="orange">{formatDisplayDate(date)}</Tag>;
        return formatDisplayDate(date);
      }
    },
    { title: '账面', dataIndex: 'totalQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '可用', dataIndex: 'availableQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '预占', dataIndex: 'reservedQty', width: 90, align: 'right', render: (value: number) => formatQty(value) },
    { title: '金额', dataIndex: 'totalAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) }
  ];

  return (
    <Card
      className="page-card"
      title={
        <Space>
          货位库存
          <Typography.Text type="secondary">仓库 → 货位 → 批次 / 效期</Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Select
            value={warehouseId || undefined}
            options={options}
            loading={warehouses.isPending}
            placeholder="选择仓库"
            style={{ width: 240 }}
            onChange={(value) => setWarehouseId(value)}
          />
          <Button icon={<ReloadOutlined />} onClick={() => stock.refetch()} loading={stock.isFetching}>
            刷新
          </Button>
        </Space>
      }
    >
      {warehouses.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="仓库加载失败"
          description={warehouses.error instanceof ApiError ? warehouses.error.friendlyMessage : String(warehouses.error)}
        />
      ) : null}
      {stock.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="库存加载失败"
          description={stock.error instanceof ApiError ? stock.error.friendlyMessage : String(stock.error)}
        />
      ) : null}
      {truncated ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="库存行数已达 200 条上限，下方统计与金额合计不完整"
          description="请按仓库/物资缩小范围查看，或分批核对；不要用本页合计做对账。"
        />
      ) : null}
      {noLocation > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${noLocation} 条库存未分货位`}
          description="未分货位的批次会按仓库汇总拣货；建议在「仓库与科室」补齐货位后，入库上架时指定货位。"
        />
      ) : null}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="库存行数" value={rows.length} suffix="行" loading={Boolean(warehouseId) && stock.isPending} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="SKU 数" value={skuCount} suffix="个" loading={Boolean(warehouseId) && stock.isPending} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="货位数" value={locations.data?.length ?? 0} suffix="个" loading={Boolean(warehouseId) && locations.isPending} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="库存金额" value={formatAmount(totalAmount)} loading={Boolean(warehouseId) && stock.isPending} />
          </Card>
        </Col>
      </Row>

      <Table<StockRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={Boolean(warehouseId) && stock.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: stock.isError ? '加载失败，请重试' : '该仓库暂无库存' }}
      />
    </Card>
  );
}
